import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { extractApiError } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import { useConversations } from "./ConversationsContext";
import ConversationList from "./messages/ConversationList";
import Thread from "./messages/Thread";
import LoadFailure from "../components/ui/LoadFailure";

/**
 * The practitioner's half of the two-way inbox: conversations on the left, the selected
 * thread on the right.
 *
 * NO LIST FETCH HAPPENS HERE. The console polls `/admin/conversations` once, in
 * admin/ConversationsContext.jsx, because the sidebar's unread pill needs the same answer
 * on every page; this screen is one more reader of it. What is local to this screen is the
 * SELECTION, and the thread pane's own delta poll (admin/messages/Thread.jsx).
 *
 * BUG-051 — A FAILED READ IS NOT AN EMPTY INBOX. `msg.empty` ("no conversations yet") is a
 * claim about the practice; it is made only when a read actually SUCCEEDED and returned
 * nothing. When the read failed, the failure is what is on screen, with a retry. A
 * practitioner told "no messages" by a 500 stops looking, and the enquiry sits unanswered.
 *
 * DEEP LINK `?c=<id>`. The practitioner's notice email links straight at one conversation
 * (modules/messaging/side_effects.py). The query parameter SEEDS the selection rather than
 * owning it: clicking another row selects it immediately and rewrites the parameter, so
 * the address bar stays truthful without the render depending on the router having
 * re-rendered first.
 */
export default function MessagesAdmin() {
  const t = useT("admin");
  const tc = useT("common");
  const { conversations, loadError, refresh, patchConversation } = useConversations();
  const [params, setParams] = useSearchParams();
  const linkedId = params.get("c");
  const [selectedId, setSelectedId] = useState(linkedId);

  // A second notice, opened while the inbox is already mounted, changes the parameter
  // under a live component — that is a request to switch threads.
  useEffect(() => {
    if (linkedId) setSelectedId(linkedId);
  }, [linkedId]);

  const select = useCallback((id) => {
    setSelectedId(id);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("c", id);
      return next;
    }, { replace: true });
  }, [setParams]);

  const selected = conversations.find((c) => c.id === selectedId) || null;

  return (
    <div data-testid="admin-messages">
      <h1 className="font-display text-4xl text-eh-ink">{t("msg.title")}</h1>
      <p className="text-eh-muted mt-1">{t("msg.subtitle")}</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div
          className="lg:col-span-5 bg-eh-cream border border-eh-border-soft rounded-2xl overflow-hidden divide-y divide-eh-border-soft"
          data-testid="admin-messages-list"
        >
          <LoadFailure
            message={loadError ? extractApiError(loadError, tc("errors.loadFailed"), tc) : ""}
            onRetry={refresh}
            retryLabel={tc("actions.retry")}
            testId="admin-messages-load-error"
            retryTestId="admin-messages-retry"
            className="rounded-none border-0"
          />
          {!loadError && conversations.length === 0 && (
            <div className="p-8 text-center text-sm text-eh-muted">{t("msg.empty")}</div>
          )}
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            onSelect={select}
          />
        </div>

        <div
          className="lg:col-span-7 bg-eh-cream border border-eh-border-soft rounded-2xl p-6 min-h-[300px]"
          data-testid="admin-message-detail"
        >
          {!selectedId ? (
            <div className="h-full flex items-center justify-center text-sm text-eh-muted">
              {t("msg.selectToRead")}
            </div>
          ) : (
            // `key` remounts the pane per conversation: its cursors are refs, and a ref
            // that survived a switch would read one client's thread with another's marker.
            <Thread
              key={selectedId}
              conversationId={selectedId}
              summary={selected}
              onPatch={patchConversation}
              onSent={refresh}
            />
          )}
        </div>
      </div>
    </div>
  );
}
